import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';
import { VList, type VListHandle } from 'virtua';

import type { AssetsSegmentSchemaPatched } from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import type {
  AssetsSegmentSchema,
  AssetsSegmentSchemaWithId,
} from '@iconik-io/iconik-api/src/services/assets';
import { segmentColors } from '@iconik-io/shared-lib/src/react/components/shared/segment/constants';
import { useVirtualizedScrollToIds } from '@iconik-io/shared-lib/src/react/hooks/use_virtualized_scroll_to_ids';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import {
  selectAssetVersionsExtended,
  selectAssetVersionsExtendedMap,
} from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';
import { getProxyMediaType } from '@iconik-io/shared-lib/src/utils/get_proxy_media_type';
import { seekableMediaTypes } from '@iconik-io/shared-lib/src/utils/media_type';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import { AssetSegmentForm } from '../../components/asset_segment_form/asset_segment_form';
import DeleteCommentModal from '../../components/modals/delete_objects/comment';
import ModalsManager from '../../components/modals/modals_manager/modals_manager';
import NoContentPanel from '../../components/no_content_panel/no_content_panel';
import Segment from '../../components/segment/segment';
import SelectFieldColorOption from '../../components/select_field_custom_options/option_color_component/option_color_component';
import SelectFieldIconOption from '../../components/select_field_custom_options/option_icon_component/option_icon_component';
import SelectFieldColorValue from '../../components/select_field_custom_values/select_field_color_value/select_field_color_value';
import TriggerContextMenu from '../../components/trigger_context_menu/trigger_context_menu';
import {
  ORDER_DATE_CREATED,
  ORDER_TIME_START_MILLISECONDS,
} from '../../constants/data';
import { COMMENTS } from '../../constants/help_urls';
import { DELETE_COMMENT_MODAL } from '../../constants/modals';
import VARIANTS from '../../constants/variants';
import { segmentTrackEvent } from '../../ducks/analytics_tracking/thunks';
import { getFilteredAssetCommentIds } from '../../ducks/asset_comments/selectors';
import {
  setColorFilter,
  setCommentsVersionFilter,
  setSortField,
  setTextFilter,
} from '../../ducks/asset_comments/slice';
import { createComment, editComment } from '../../ducks/asset_comments/thunks';
import { openModal } from '../../ducks/modals_manager/slice';
import {
  selectAsset,
  selectAssetFiles,
  selectAssetFormats,
  selectAssetProxy,
} from '../../ducks/pages/asset/selectors';
import { combineSearchItemFromAsset } from '../../ducks/search/selectors';
import { highlightSegmentsTemporarily } from '../../ducks/segments/thunks';
import { selectIsSharePage } from '../../ducks/share/selectors';
import { selectCurrentVersionId } from '../../ducks/version/selectors';
import type { IconType } from '../../icons/constants';
import { ICONS } from '../../icons/icon';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import SelectFieldWrapper from '../../ui/forms/field_wrappers/select/select';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import SearchInput from '../../ui/search_input/search_input';
import { selectUserRoles } from '../../utils/get_user_roles';
import { getSearchItemMediaType } from '../../utils/media_type';
import AssetCommentsGearboxContextMenu from './gearbox_context_menu';

import commentStyles from '../../components/segment/segment.module.css';
import styles from './asset_comments.module.css';

const MenuTriggerButton: FC = () => (
  <Button
    className={styles.header_button}
    noBorder
    wideBasis={false}
    iconName="menu_context"
    variant={VARIANTS.WHITE}
  />
);

interface VersionOptionInterface {
  label?: string;
  value?: string;
  icon?: IconType | null;
}

export const AssetCommentsPanel: FC = () => {
  const dispatch = useAppDispatch();

  const { store: unifiedPlayerStore } = useBasePlayerContext();
  const roles = useAppSelector(selectUserRoles);
  const { id: assetId, versionId } = useParams({ strict: false });
  const asset = useAppSelector((state) => selectAsset(state, assetId));
  const assetProxy = useAppSelector((state) =>
    selectAssetProxy(state, assetId, versionId),
  );
  const assetFormats = useAppSelector((state) =>
    selectAssetFormats(state, assetId, versionId),
  );
  const assetFiles = useAppSelector((state) =>
    selectAssetFiles(state, assetId, versionId),
  );
  const extendedVersions = useAppSelector((state) =>
    selectAssetVersionsExtended(state, assetId),
  );
  const extendedVersionsMap = useAppSelector((state) =>
    selectAssetVersionsExtendedMap(state, assetId),
  );
  const currentVersionId = useAppSelector((state) =>
    selectCurrentVersionId(state, assetId, versionId),
  );

  const sortField = useAppSelector(
    (state) => state.assetCommentsReducer.sortField,
  );
  const versionFilter = useAppSelector(
    (state) => state.assetCommentsReducer.versionFilter,
  );
  const loading = useAppSelector((state) => state.assetCommentsReducer.loading);
  const flatIds = useAppSelector((state) =>
    getFilteredAssetCommentIds(state, assetId ?? '', versionId),
  );
  const highlightedSegmentIds = useAppSelector(
    (state) => state.segments.highlightedSegmentIds,
  );
  const segments = useAppSelector((state) => state.entities.segments);

  const share = useAppSelector((state) => state.share.share);
  const isSharePage = useAppSelector(selectIsSharePage);

  let canApproveComments = roles.canWriteSegments;
  if (isSharePage && share) {
    canApproveComments = share.allow_approving_comments;
  }

  const [scrollToId, setScrollToId] = useState<string | undefined>('undefined');

  const vListRef = useRef<VListHandle>(null);
  const { setScrollToIds } = useVirtualizedScrollToIds({
    virtualizer: vListRef.current,
    ids: flatIds,
  });

  useEffect(() => {
    setScrollToIds(highlightedSegmentIds);
  }, [highlightedSegmentIds]);

  useEffect(() => {
    if (scrollToId) {
      setScrollToIds([scrollToId]);
    }
  }, [scrollToId]);

  const handleReply = useCallback(
    async (
      _id: string,
      values: AssetsSegmentSchema,
      shareLinkUserEmail?: string,
    ): Promise<void> => {
      if (!asset) {
        return;
      }
      await dispatch(createComment(asset.id, values, shareLinkUserEmail));
      unifiedPlayerStore.segment.clearCurrentSegment();
      unifiedPlayerStore.segment.annotation.clearPersonalDraftAnnotation();
      const mediaType = getSearchItemMediaType(
        combineSearchItemFromAsset(asset, assetFormats, assetFiles),
      );
      dispatch(
        segmentTrackEvent({
          eventName: 'Segment Created',
          properties: {
            'Media Type': mediaType,
            'Segment Type': 'Comment',
            'Segment Color': values.segment_color,
            'Annotation Tool Used': Boolean(values.drawing),
            'Has Timecodes': false,
            'Is Reply': true,
          },
        }),
      );
    },
    [asset, assetFiles, assetFormats],
  );

  const handleEdit = useCallback(
    (
      originalSegment: SegmentEntityWithIdNormalized,
      values: Partial<AssetsSegmentSchemaPatched>,
    ) =>
      assetId
        ? dispatch(editComment(assetId, originalSegment.id, values))
        : Promise.resolve(),
    [assetId],
  );

  const handleDelete = useCallback(
    (segmentId: string) => {
      dispatch(
        openModal({
          id: DELETE_COMMENT_MODAL,
          contextItems: [segments[segmentId]],
        }),
      );
    },
    [segments],
  );

  const handleCheck = useCallback(
    (segmentId: string, name: string, value: boolean) => {
      if (!assetId) {
        return;
      }
      dispatch(editComment(assetId, segmentId, { [name]: value }));
    },
    [assetId],
  );

  const handleSearch = useCallback((value: string) => {
    dispatch(setTextFilter({ filters: value }));
  }, []);

  const handleFilter = useCallback(
    (name: string, options: { value: string }[]) => {
      if (options && name === 'segment_color') {
        const filterValues = options.map((option) => option.value);
        dispatch(setColorFilter({ filters: filterValues }));
      }
    },
    [],
  );

  const seekable = seekableMediaTypes.has(getProxyMediaType(assetProxy));

  const currentVersion = currentVersionId
    ? extendedVersionsMap[currentVersionId]
    : null;

  const showVersionsDropdown =
    roles.webCanViewVersions && currentVersion && extendedVersions.length > 1;

  const versionOptions: VersionOptionInterface[] = extendedVersions
    .filter((version) => version.status === 'ACTIVE' && !version.promotedTo)
    .map((version) => ({
      value: version.id,
      label: version.name,
      icon: version.id === currentVersionId ? ICONS.Eye : null,
      reserveIconSpace: true,
    }))
    .toReversed()
    .concat({ label: 'All', value: 'all', icon: null, reserveIconSpace: true });

  const handleChangeOrder = useCallback(
    (value: keyof AssetsSegmentSchema | undefined) =>
      dispatch(setSortField({ fieldName: value })),
    [],
  );

  const handleSetCommentsVersionFilter = useCallback(
    (value: string | undefined) =>
      dispatch(setCommentsVersionFilter({ versionId: value })),
    [],
  );

  const handleSegmentCreated = useCallback(
    (segment: AssetsSegmentSchemaWithId) => {
      dispatch(highlightSegmentsTemporarily([segment.id], 3000));
      setScrollToId(segment.id);

      unifiedPlayerStore.segment.setSelectedSegmentIds([segment.id]);
      unifiedPlayerStore.segment.setCurrentSegment(segment);
    },
    [],
  );

  if (!assetId || loading[assetId] || !asset) {
    return <LoadingBar />;
  }

  const segmentElements = flatIds.map((flatId, index) => {
    const segment = segments[flatId];

    const className = classNames(
      segment.parent_id ? commentStyles.children : undefined,
      highlightedSegmentIds.includes(segment.id)
        ? styles.highlight
        : styles.highlight_disabled,
      index === 0 ? styles.first_comment : undefined,
    );

    return (
      <Segment
        key={segment.id}
        assetId={assetId}
        segment={segment}
        className={className}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onReply={handleReply}
        checked={segment.segment_checked}
        onCheck={canApproveComments ? handleCheck : undefined}
        version={
          segment.version_id && extendedVersions.length > 1
            ? extendedVersionsMap[segment.version_id]
            : undefined
        }
        isCurrentVersion={
          !currentVersionId ||
          currentVersionId === segment.version_id ||
          !segment.version_id
        }
      />
    );
  });

  const isCommentVersionMatchingPlayer =
    versionFilter === undefined ||
    versionFilter === currentVersionId ||
    versionFilter === 'all';

  return (
    <>
      <ModalsManager
        modals={{
          [DELETE_COMMENT_MODAL]: DeleteCommentModal,
        }}
      />
      <div className={styles.list_wrapper}>
        <div className={styles.header}>
          <div className={styles.header_top}>
            <SearchInput onSearch={handleSearch} className={styles.search} />
            <div className={styles.header_buttons}>
              {window.PLATFORM === 'WEB' ? (
                <TriggerContextMenu
                  triggerElement={<MenuTriggerButton />}
                  contextMenu={AssetCommentsGearboxContextMenu}
                  contextItems={[asset]}
                />
              ) : null}
            </div>
          </div>

          <div className={styles.search_fields}>
            <div className={styles.search_fields_row}>
              {showVersionsDropdown ? (
                <div className={styles.versions}>
                  <SelectFieldWrapper
                    options={versionOptions}
                    onChange={handleSetCommentsVersionFilter}
                    name="versions"
                    value={versionFilter || currentVersionId || undefined}
                    optionComponent={SelectFieldIconOption}
                  />
                </div>
              ) : null}

              <div className={styles.color_search}>
                <SelectFieldWrapper
                  canClear
                  isMulti
                  name="segment_color"
                  placeholder="Color"
                  onChange={(value: any) =>
                    handleFilter('segment_color', value)
                  }
                  options={segmentColors}
                  valueComponent={SelectFieldColorValue}
                  optionComponent={SelectFieldColorOption}
                  aria={{
                    label: 'Filter comments by color',
                  }}
                />
              </div>
              {seekable ? (
                <div className={styles.sort_by}>
                  <SelectFieldWrapper
                    canClear={false}
                    options={[
                      { label: 'By Date', value: ORDER_DATE_CREATED },
                      {
                        label: 'By Timecode',
                        value: ORDER_TIME_START_MILLISECONDS,
                      },
                    ]}
                    onChange={handleChangeOrder}
                    name="sort_by"
                    value={sortField}
                    aria={{
                      label: 'Sort comments by',
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {flatIds.length > 0 ? (
          <VList ref={vListRef} role="list" aria-label="Comments list">
            {segmentElements}
          </VList>
        ) : (
          <NoContentPanel
            title="Leave detailed and time-based feedback for your team or clients"
            linkText="Learn more"
            linkHref={COMMENTS}
          />
        )}
      </div>
      {roles.canCreateSegments && isCommentVersionMatchingPlayer ? (
        <div className={styles.comment_form_wrapper}>
          <AssetSegmentForm
            formClassName={styles.comment_form}
            assetVersion={currentVersionId}
            onSegmentCreated={handleSegmentCreated}
          />
        </div>
      ) : null}
    </>
  );
};
